import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
  MessageSquare, Send, CheckCircle, 
  ChevronLeft, User, XCircle 
} from 'lucide-react';

import { usePermission } from '../../hooks/usePermission';

// --- Badges ---
const PriorityBadge = ({ p }) => {
  const colors = { low: 'bg-blue-100 text-blue-700', medium: 'bg-yellow-100 text-yellow-700', high: 'bg-orange-100 text-orange-700', critical: 'bg-red-100 text-red-700' };
  return <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${colors[p] || colors.medium}`}>{p}</span>;
};

const StatusBadge = ({ s }) => {
    const colors = { open: 'bg-green-100 text-green-700', in_progress: 'bg-blue-100 text-blue-700', awaiting_reply: 'bg-purple-100 text-purple-700', resolved: 'bg-slate-100 text-slate-700', closed: 'bg-slate-200 text-slate-500' };
    return <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${colors[s] || colors.open}`}>{s?.replace('_', ' ')}</span>;
};

const TicketManagerPage = () => {
  const queryClient = useQueryClient();
  const [view, setView] = useState('list');
  const [activeTicketId, setActiveTicketId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [replyMsg, setReplyMsg] = useState('');
  const scrollRef = useRef(null);
  const { can } = usePermission();

  // --- QUERIES ---
  // 1. Fetch Tickets
  const { data: tickets = [] } = useQuery({
    queryKey: ['adminTickets', statusFilter],
    queryFn: async () => {
        try {
            const res = await axiosClient.get('/admin/support/tickets', { params: { status: statusFilter } });
            return Array.isArray(res) ? res : [];
        } catch { return []; }
    }
  });

  // 2. Fetch Chat (If active)
  const { data: conversation } = useQuery({
    queryKey: ['adminTicketChat', activeTicketId],
    queryFn: () => axiosClient.get(`/admin/support/tickets/${activeTicketId}`),
    enabled: !!activeTicketId,
    refetchInterval: 5000
  });

  // 3. Fetch Staff (For Assignment Dropdown)
  const { data: staffList = [] } = useQuery({
      queryKey: ['staffList'],
      queryFn: () => axiosClient.get('/admin/staff')
  });

  // --- MUTATIONS ---
  const replyMutation = useMutation({
      mutationFn: (msg) => axiosClient.post(`/admin/support/tickets/${activeTicketId}/reply`, { message: msg }),
      onSuccess: () => {
          queryClient.invalidateQueries(['adminTicketChat', activeTicketId]);
          setReplyMsg('');
      }
  });

  const assignMutation = useMutation({
      mutationFn: ({ uid, staffId }) => axiosClient.put(`/admin/support/tickets/${uid}/assign`, { staff_id: staffId }),
      onSuccess: () => { queryClient.invalidateQueries(['adminTickets']); alert("Assigned!"); }
  });

  const statusMutation = useMutation({
      mutationFn: ({ uid, status }) => axiosClient.put(`/admin/support/tickets/${uid}/status`, { status }),
      onSuccess: () => { queryClient.invalidateQueries(['adminTickets']); if (view==='chat') setView('list'); }
  });

  // Auto-scroll chat
  useEffect(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conversation]);

  // --- RENDERERS ---

  // 1. CHAT VIEW
  if (view === 'chat' && activeTicketId) {
      if (!conversation) return <div className="p-10 text-center">Loading...</div>;
      
      return (
        <div className="h-[calc(100vh-140px)] flex flex-col bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden animate-in slide-in-from-right-4">
             {/* Header */}
             <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                 <div className="flex items-center">
                    <button onClick={() => setView('list')} className="mr-3 p-1.5 hover:bg-slate-200 rounded-lg text-slate-500"><ChevronLeft className="w-5 h-5"/></button>
                    <div>
                        <h2 className="font-bold text-slate-800 text-lg flex items-center">
                            {conversation.ticket.subject}
                            <span className="ml-3"><PriorityBadge p={conversation.ticket.priority} /></span>
                        </h2>
                        <p className="text-xs text-slate-500 font-mono">ID: {activeTicketId}</p>
                    </div>
                 </div>
                 <div className="flex items-center gap-2">
                     <StatusBadge s={conversation.ticket.status} />
                     <button onClick={() => statusMutation.mutate({ uid: activeTicketId, status: 'resolved' })} className="p-1.5 hover:bg-green-100 text-green-600 rounded" title="Mark Resolved"><CheckCircle className="w-5 h-5"/></button>
                     <button onClick={() => statusMutation.mutate({ uid: activeTicketId, status: 'closed' })} className="p-1.5 hover:bg-slate-200 text-slate-600 rounded" title="Close Ticket"><XCircle className="w-5 h-5"/></button>
                 </div>
             </div>

             {/* Messages */}
             <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/50" ref={scrollRef}>
                 {conversation.replies.map((msg, i) => (
                     <div key={i} className={`flex ${msg.is_staff_reply ? 'justify-end' : 'justify-start'}`}>
                         <div className={`max-w-[70%] ${msg.is_staff_reply ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200'} p-4 rounded-2xl shadow-sm relative group`}>
                             <div className="flex justify-between items-center mb-1 text-[10px] opacity-70 font-bold uppercase tracking-wider">
                                 <span>{msg.is_staff_reply ? 'Staff' : 'Customer'}</span>
                                 <span>{new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                             </div>
                             <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                         </div>
                     </div>
                 ))}
             </div>

             {/* Reply Box */}
             <div className="p-4 bg-white border-t border-slate-200">
                 <form onSubmit={(e) => { e.preventDefault(); if(replyMsg.trim()) replyMutation.mutate(replyMsg); }} className="flex gap-2">
                     <input className="flex-1 p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Reply to customer..." value={replyMsg} onChange={(e) => setReplyMsg(e.target.value)} />
                     <button disabled={replyMutation.isLoading || !replyMsg.trim()} className="bg-blue-600 text-white p-3 rounded-xl hover:bg-blue-700 disabled:opacity-50"><Send className="w-5 h-5" /></button>
                 </form>
             </div>
        </div>
      );
  }

  // 2. LIST VIEW
  return (
    <div className="space-y-6 animate-fade-in pb-10">
      <div className="flex justify-between items-center">
         <div><h1 className="text-2xl font-bold text-slate-800">Support Desk</h1><p className="text-sm text-slate-500">Manage customer inquiries.</p></div>
         <div className="flex gap-2">
            <select className="p-2 border rounded-lg bg-white text-sm" onChange={e => setStatusFilter(e.target.value)}>
                <option value="">All Status</option>
                <option value="open">Open</option>
                <option value="awaiting_reply">Pending User</option>
                <option value="resolved">Resolved</option>
            </select>
         </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase">
             <tr>
                <th className="px-6 py-4">Subject</th>
                <th className="px-6 py-4">Customer</th>
                <th className="px-6 py-4">Assigned To</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Priority</th>
                <th className="px-6 py-4">Updated</th>
                <th className="px-6 py-4 text-right">Action</th>
             </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
             {tickets.map((t) => (
                <tr key={t.ticket_uid} className="hover:bg-slate-50 transition-colors">
                   <td className="px-6 py-4 font-semibold text-slate-700 cursor-pointer" onClick={() => { setActiveTicketId(t.ticket_uid); setView('chat'); }}>{t.subject}</td>
                   <td className="px-6 py-4 text-sm text-slate-600">{t.customer_name} <span className="text-xs text-slate-400 block">{t.customer_email}</span></td>
                   <td className="px-6 py-4">
                       <div className="relative group/assign">
                           <button className={`text-xs font-bold px-2 py-1 rounded border flex items-center ${t.assigned_staff_name ? 'bg-slate-100 border-slate-300 text-slate-700' : 'bg-yellow-50 border-yellow-200 text-yellow-700'}`}>
                               <User className="w-3 h-3 mr-1"/> {t.assigned_staff_name || 'Unassigned'}
                           </button>
                           {/* Quick Assign Dropdown (Simple implementation) */}
                           <select 
                             className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                             onChange={(e) => assignMutation.mutate({ uid: t.ticket_uid, staffId: parseInt(e.target.value) })}
                             value={t.assigned_staff_id || ''}
                             disabled={!can('tickets:manage')}
                           >
                              <option value="">Assign Staff</option>
                              {staffList.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                           </select>
                       </div>
                   </td>
                   <td className="px-6 py-4"><StatusBadge s={t.status} /></td>
                   <td className="px-6 py-4"><PriorityBadge p={t.priority} /></td>
                   <td className="px-6 py-4 text-sm text-slate-500">{new Date(t.updated_at).toLocaleDateString()}</td>
                   <td className="px-6 py-4 text-right">
                      <button onClick={() => { setActiveTicketId(t.ticket_uid); setView('chat'); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><MessageSquare className="w-4 h-4"/></button>
                   </td>
                </tr>
             ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TicketManagerPage;